/**
 * Alpine component behind the ColorPicker field.
 *
 * Keeps a native `<input type="color">` and a free-text input in sync with a single
 * entangled Livewire value. The text input accepts hex, rgb(), rgba(), hsl(), hsla()
 * and named CSS colours; every accepted value is normalised to `#rrggbb` before it is
 * written back, because `<input type="color">` cannot represent anything else — and
 * because the server side validates hex.
 *
 * The model is normalised on every keystroke, but the TEXT is only normalised on blur:
 * while the field has focus the user keeps whatever notation they typed. Rewriting it
 * live would replace a half-typed '#4f4' with '#44ff44' under the cursor.
 *
 * An unparseable text value is never written to the model; it is reverted on blur.
 * A nullable field is the one exception: it may start empty and clearing the text
 * writes null — the default colour is never pushed into a nullable model.
 */
export default (entangled, config = {}) => ({
  model: entangled,
  text: '',

  init() {
    const initial = this.toHex(this.model)

    if (initial === null && config.nullable) {
      this.text = ''
    } else {
      const hex = initial ?? this.toHex(config.default) ?? '#000000'

      if (hex !== this.model) {
        this.model = hex
      }

      this.text = hex
    }

    // Text -> model, but only for values we can actually parse — except that a
    // nullable field may be cleared.
    this.$watch('text', (value) => {
      const hex = this.toHex(value)

      if (hex !== null) {
        if (hex !== this.model) {
          this.model = hex
        }

        return
      }

      if (config.nullable && value.trim() === '' && this.model !== null) {
        this.model = null
      }
    })

    // Model -> text (native picker, swatch click, or a server-side change).
    //
    // The comparison is between PARSED values, not raw strings: the watcher above writes
    // the model on every keystroke, and that write lands back here. Comparing raw would
    // replace the half-typed '#4f4' with '#44ff44' mid-word, and would rewrite a typed
    // 'rgb(79,70,229)' to hex before the user is done. If the text already denotes the
    // incoming colour, it IS the source of that colour — leave it as typed and let
    // normalizeOnBlur() canonicalise it later.
    this.$watch('model', (value) => {
      const hex = this.toHex(value)

      if (hex !== null) {
        if (this.toHex(this.text) !== hex) {
          this.text = hex
        }
      } else if (config.nullable && this.text.trim() !== '') {
        this.text = ''
      }
    })
  },

  /**
   * Open the native colour dialog. Bound to the whole text input, not just the small
   * swatch, so the entire field is a click target. `showPicker()` is the explicit API;
   * older browsers fall back to a synthetic click on the hidden colour input.
   */
  openPicker() {
    const native = this.$refs.native

    if (!native || native.disabled || native.readOnly) {
      return
    }

    if (typeof native.showPicker === 'function') {
      native.showPicker()

      return
    }

    native.click()
  },

  /**
   * Apply a preset swatch.
   */
  pick(color) {
    const hex = this.toHex(color)

    if (hex !== null) {
      this.model = hex
      this.text = hex
    }
  },

  /**
   * Blur is where the text input becomes canonical `#rrggbb`. While the field has focus
   * the user's own notation is left untouched (see the model watcher), so this is the
   * point at which 'rgb(79,70,229)' or '#4f4' turns into hex.
   *
   * Typing garbage should not silently clear the colour — the last good value goes back
   * in. An empty nullable field is not garbage; it stays empty.
   */
  normalizeOnBlur() {
    if (config.nullable && this.text.trim() === '') {
      this.text = ''

      return
    }

    this.text = this.toHex(this.text) ?? this.toHex(this.model) ?? ''
  },

  /**
   * Normalise any supported CSS colour notation to `#rrggbb`, or null when it cannot
   * be parsed. Separators are normalised first so the modern space/slash notation
   * (`rgb(79 70 229 / .5)`) reaches the parsers in the comma form they expect.
   */
  toHex(value) {
    if (typeof value !== 'string') {
      return null
    }

    const input = value
      .trim()
      .toLowerCase()
      .replace(/\s*([(),/])\s*/g, '$1')
      .replace(/\s+/g, ',')
      .replace(/\//g, ',')

    if (input === '') {
      return null
    }

    return this.fromHex(input)
      ?? this.fromRgb(input)
      ?? this.fromHsl(input)
      ?? this.fromNamedColor(input)
  },

  /**
   * Only the 6-digit form is accepted. Three-digit hex is deliberately NOT supported: it
   * is a valid prefix of a 6-digit code, so typing or deleting '#444333' unavoidably
   * passes through '#444' and the colour would jump to #444444 on the way. A trailing
   * alpha pair (#rrggbbaa) is accepted but dropped.
   */
  fromHex(input) {
    const match = input.match(/^#([0-9a-f]{6})(?:[0-9a-f]{2})?$/)

    return match ? `#${match[1]}` : null
  },

  fromRgb(input) {
    const match = input.match(/^rgba?\((-?\d+),(-?\d+),(-?\d+)(?:,[\d.]+%?)?\)$/)

    if (!match) {
      return null
    }

    return this.channelsToHex([match[1], match[2], match[3]].map(Number))
  },

  fromHsl(input) {
    const match = input.match(/^hsla?\((-?\d+),(-?\d+)%,(-?\d+)%(?:,[\d.]+%?)?\)$/)

    if (!match) {
      return null
    }

    const hue = ((Number(match[1]) % 360) + 360) % 360
    const saturation = this.clamp(Number(match[2]), 0, 100) / 100
    const lightness = this.clamp(Number(match[3]), 0, 100) / 100

    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation
    const second = chroma * (1 - Math.abs(((hue / 60) % 2) - 1))
    const base = lightness - chroma / 2

    const sector = Math.floor(hue / 60) % 6
    const sectors = [
      [chroma, second, 0],
      [second, chroma, 0],
      [0, chroma, second],
      [0, second, chroma],
      [second, 0, chroma],
      [chroma, 0, second],
    ]

    return this.channelsToHex(sectors[sector].map((part) => Math.round((part + base) * 255)))
  },

  /**
   * Named colours ("rebeccapurple") are resolved by the browser: assigning to a style
   * declaration yields an rgb() string for anything it recognises, and leaves the
   * value empty for anything it does not.
   */
  fromNamedColor(input) {
    if (typeof document === 'undefined' || !/^[a-z]+$/.test(input)) {
      return null
    }

    const probe = document.createElement('div')
    probe.style.color = ''
    probe.style.color = input

    if (probe.style.color === '') {
      return null
    }

    return this.fromRgb(probe.style.color.replace(/\s+/g, '')) ?? null
  },

  channelsToHex(channels) {
    return `#${channels
      .map((channel) => this.clamp(channel, 0, 255).toString(16).padStart(2, '0'))
      .join('')}`
  },

  clamp(value, min, max) {
    if (Number.isNaN(value)) {
      return min
    }

    return Math.min(Math.max(Math.round(value), min), max)
  },
})
